/**
 * AUDIO — owner: audio team.
 *
 * Everything is synthesised procedurally at runtime through the Web Audio API.
 * No sample files, no network assets, no npm packages, no Math.random().
 *
 * Lifecycle
 * ---------
 * Browsers forbid starting audio without a user gesture, and the headless capture
 * harness runs muted and never gestures. So: no AudioContext is created until
 * `resume()` is called. Until then `enabled` is false and every entry point —
 * play / gunshot / impact / footstep / setListener / music, plus every event-bus
 * handler — is a silent no-op that cannot throw. The offline analysis used by the
 * `audio_debug` shot runs through OfflineAudioContext, which needs no gesture, so
 * the diagnostics still work in a completely inert run.
 *
 * Frozen API (CONTRACTS.md):
 *   enabled, resume(), play(name, opts), gunshot(weapon, opts),
 *   impact(surface, opts), footstep(surface, opts),
 *   setListener(position, quaternion, velocity), music(state)
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { registerShot, look } from '../core/Shots.js';

import { Buses } from './Buses.js';
import { Zones, ZONE_ORDER } from './Zones.js';
import { Spatial } from './Spatial.js';
import { Music, MUSIC_STATES } from './Music.js';
import { Ambience } from './Ambience.js';
import { AudioDebugOverlay } from './debugOverlay.js';

import { Baker, renderOffline } from './synth/offline.js';
import { softClip, playData } from './synth/nodes.js';
import { getSound } from './banks/index.js';
import { buildGunshot, classify, specFor, WEAPON_CLASSES } from './banks/Weapons.js';
import { SURFACES } from './banks/Impacts.js';
import { STEP_SURFACES } from './banks/Footsteps.js';

const BULLET_SPEED = 880;
const SPEED_OF_SOUND = 343;
const DISTANT_THRESHOLD = 55;      // metres beyond which a shot uses the distant voicing
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _prevCam = new THREE.Vector3();

/** Bake plan: [class, mode, variants]. Only the common cases are pre-rendered. */
const EAGER_GUN_BAKES = [
  ['rifle', 'normal', 5], ['smg', 'normal', 5], ['pistol', 'normal', 4],
  ['shotgun', 'normal', 4], ['sniper', 'normal', 3],
  ['rifle', 'suppressed', 3], ['smg', 'suppressed', 3], ['pistol', 'suppressed', 3],
  ['rifle', 'distant', 3], ['smg', 'distant', 2], ['sniper', 'distant', 2],
];
const EAGER_BANK_BAKES = [
  'impact_concrete', 'impact_metal', 'impact_wood', 'impact_dirt', 'impact_flesh',
  'crack', 'ricochet', 'mag_release', 'mag_out', 'mag_in', 'mag_seat', 'bolt_release',
  'shell_drop', 'hitmarker', 'headshot',
];

export function createAudioSystem(config = {}) {
  /* ------------------------------------------------------------------ *
   * State that exists before (and without) any AudioContext
   * ------------------------------------------------------------------ */
  let ctxRef = null;
  let ac = null;
  let buses = null;
  let zones = null;
  let spatial = null;
  let music = null;
  let ambience = null;
  let running = false;
  let starting = false;
  let disposed = false;

  const baker = new Baker();
  const bakedGuns = new Map();      // "class|mode" -> AudioBuffer[]
  const bakedBank = new Map();      // name -> AudioBuffer[]
  const bakeRequested = new Set();
  const rng = makeRng(config.seed ?? 0xaad10);
  const overlay = new AudioDebugOverlay();

  const stats = { shots: 0, impacts: 0, steps: 0, plays: 0, dropped: 0, resumeFails: 0 };
  const eventCounts = { fire: 0, hit: 0, step: 0, land: 0, explosion: 0, reload: 0, damage: 0 };

  let gait = 'walk';
  let playerSpeed = 0;
  let crouched = false;
  let lastState = 'idle';
  let combatUntil = 0;
  let explicitMusic = null;
  let desiredMusic = 'ambient';
  let reloadToken = 0;
  const timers = new Set();
  let analysisStarted = false;
  let analysisItems = [];
  let camVelInit = false;
  let shotWrapDone = false;
  let framesSeen = 0;

  const now = () => (ac ? ac.currentTime : 0);
  const nextRng = (seed) => (seed === undefined || seed === null ? rng : makeRng((seed >>> 0) || 1));

  function later(ms, fn) {
    const id = setTimeout(() => { timers.delete(id); try { fn(); } catch { /* never break a frame */ } }, ms);
    timers.add(id);
    return id;
  }
  function clearTimers() {
    for (const id of timers) clearTimeout(id);
    timers.clear();
  }

  /* ------------------------------------------------------------------ *
   * Baking
   * ------------------------------------------------------------------ */

  function bakeGunVariants(cls, mode, count, distance = 140) {
    const key = `${cls}|${mode}`;
    if (bakeRequested.has(key)) return;
    bakeRequested.add(key);
    const W = specFor(cls, mode, distance);
    const arr = [];
    bakedGuns.set(key, arr);
    for (let i = 0; i < count; i++) {
      const seed = 0x9e37 * (i + 1) + cls.length * 131 + mode.length * 17;
      baker.enqueue(`gun:${key}:${i}`, () => renderOffline(
        W.duration + 0.12,
        (oc, dest) => {
          const clip = softClip(oc, 1.25);
          clip.connect(dest);
          buildGunshot(oc, clip, 0, makeRng(seed >>> 0), W);
        },
        { sampleRate: ac ? ac.sampleRate : 48000, normalise: true, target: 0.97 },
      )).then((buf) => { if (buf) arr.push(buf); });
    }
  }

  function bakeBankVariants(name, count) {
    if (bakeRequested.has(name)) return;
    const s = getSound(name);
    if (!s || !s.bake) return;
    bakeRequested.add(name);
    const arr = [];
    bakedBank.set(name, arr);
    const n = count ?? s.variants ?? 3;
    for (let i = 0; i < n; i++) {
      const seed = 0x51ce * (i + 3) + name.length * 977;
      baker.enqueue(`bank:${name}:${i}`, () => renderOffline(
        s.seconds + 0.05,
        (oc, dest) => {
          const clip = softClip(oc, 1.2);
          clip.connect(dest);
          s.build(oc, clip, 0, makeRng(seed >>> 0), {});
        },
        { sampleRate: ac ? ac.sampleRate : 48000, normalise: true, target: 0.97 },
      )).then((buf) => { if (buf) arr.push(buf); });
    }
  }

  function scheduleEagerBakes() {
    for (const [cls, mode, n] of EAGER_GUN_BAKES) bakeGunVariants(cls, mode, n);
    for (const name of EAGER_BANK_BAKES) bakeBankVariants(name);
  }

  /* ------------------------------------------------------------------ *
   * Voice helpers
   * ------------------------------------------------------------------ */

  function vec(p) {
    if (!p) return null;
    if (p.isVector3) return p;
    if (Array.isArray(p)) return _v.set(p[0] || 0, p[1] || 0, p[2] || 0);
    if (typeof p.x === 'number') return _v.set(p.x, p.y || 0, p.z || 0);
    return null;
  }

  /**
   * Play a bank entry: baked variant if one exists, live node graph otherwise.
   * Returns the voice, or null if it was dropped / audio is off.
   */
  function playSound(name, opts = {}) {
    if (!running) return null;
    const s = getSound(name);
    if (!s) return null;
    stats.plays++;

    const r = nextRng(opts.seed);
    const pos = vec(opts.position);
    const spatialise = !!pos && opts.spatial !== false;
    const voice = spatial.voice({
      position: spatialise ? pos.clone() : null,
      velocity: opts.velocity || null,
      bus: opts.bus || s.bus,
      volume: (opts.volume ?? 1) * (opts.gainScale ?? 1),
      duration: opts.duration ?? s.seconds,
      refDistance: opts.refDistance ?? (s.bus === 'ui' ? 1000 : 4),
      maxDistance: opts.maxDistance ?? 400,
      send: opts.send ?? (s.bus === 'ui' ? 0.05 : 0.42),
      priority: opts.priority ?? 0.5,
      spatial: spatialise,
      occlusion: opts.occlusion !== false,
    });
    if (!voice) { stats.dropped++; return null; }

    const t0 = now() + 0.004 + (opts.delay || 0);
    const variants = bakedBank.get(name);
    if (variants && variants.length) {
      const buf = variants[Math.floor(r() * variants.length) % variants.length];
      playData(ac, buf, voice.input, t0, {
        rate: (opts.pitch ?? 1) * (1 + (r() * 2 - 1) * 0.028),
      });
    } else {
      if (s.bake) bakeBankVariants(name);
      const { volume, ...rest } = opts;
      try { s.build(ac, voice.input, t0, r, rest); } catch { /* a bad build must not break the frame */ }
    }
    return voice;
  }

  /* ------------------------------------------------------------------ *
   * The frozen API
   * ------------------------------------------------------------------ */

  const api = {
    /* --- state ------------------------------------------------------ */
    get enabled() { return running; },
    set enabled(v) { if (!v) api.mute(true); },
    get context() { return ac; },
    get state() { return ac ? ac.state : 'closed'; },

    /**
     * Create and start the audio graph. Must be called from a user gesture.
     * Safe to call repeatedly; resolves false if audio cannot start.
     * @returns {Promise<boolean>}
     */
    async resume() {
      if (disposed) return false;
      if (running && ac && ac.state === 'running') return true;
      if (starting) return running;
      starting = true;
      try {
        if (!ac) {
          const AC = typeof AudioContext !== 'undefined' ? AudioContext
            : (typeof webkitAudioContext !== 'undefined' ? webkitAudioContext : null);
          if (!AC) { starting = false; return false; }
          ac = new AC({ latencyHint: 'interactive' });
          buses = new Buses(ac, { masterLevel: config.masterLevel ?? 0.85 });
          zones = new Zones(ac, buses);
          spatial = new Spatial(ac, buses, zones, { maxVoices: config.maxVoices ?? 44 });
          spatial.attach(ctxRef);
          music = new Music(ac, buses);
          ambience = new Ambience(ac, buses);
          ambience.start();
          ambience.gunfireCb = () => distantGunfire();
        }
        if (ac.state === 'suspended') await ac.resume();
        running = ac.state === 'running';
        if (running) {
          music.set(explicitMusic || desiredMusic);
          scheduleEagerBakes();
        }
      } catch (err) {
        stats.resumeFails++;
        running = false;
        if (typeof console !== 'undefined') console.warn('[audio] resume failed:', err);
      }
      starting = false;
      return running;
    },

    /** Named one-shot. Unknown names are a silent no-op. */
    play(name, opts = {}) {
      if (!running || typeof name !== 'string') return null;
      return playSound(name, opts || {});
    },

    /**
     * A gunshot. `weapon` may be a name, a weapon instance, or nothing.
     * opts: { position|origin, dir, seed, suppressed, own, distance, volume }
     */
    gunshot(weapon, opts = {}) {
      if (!running) return null;
      const o = opts || {};
      const pos = vec(o.position || o.origin);
      const worldPos = pos ? pos.clone() : null;
      const cls = classify(weapon);
      const dist = worldPos ? Math.max(0, spatial.listener.distanceTo(worldPos)) : 0;
      const own = o.own ?? (!worldPos || dist < 2.6);
      const suppressed = !!(o.suppressed ?? (weapon && weapon.suppressed));
      const mode = suppressed ? 'suppressed' : (!own && dist > DISTANT_THRESHOLD ? 'distant' : 'normal');
      stats.shots++;

      const r = nextRng(o.seed);
      const W = specFor(cls, mode, dist);
      const key = `${cls}|${mode}`;
      const variants = bakedGuns.get(key);
      if (!variants) bakeGunVariants(cls, mode, mode === 'normal' ? 4 : 3, dist);

      const closeness = own ? 1 : 1 / (1 + dist / 12);
      const voice = spatial.voice({
        position: own ? null : worldPos,
        bus: 'weapons',
        // mixLevel restores each class's relative loudness after the buffers were
        // levelled for headroom during baking
        volume: (o.volume ?? 1) * (W.mixLevel ?? 1) * (own ? 0.9 : 1),
        duration: W.duration,
        refDistance: 6,
        maxDistance: 600,
        rolloff: mode === 'distant' ? 0.55 : 1.0,
        send: own ? 0.55 : 0.5,
        priority: own ? 1 : 0.8,
        spatial: !own && !!worldPos,
        occlusion: !own,
      });
      if (!voice) { stats.dropped++; return null; }

      const t0 = now() + 0.004;
      if (variants && variants.length) {
        const buf = variants[Math.floor(r() * variants.length) % variants.length];
        playData(ac, buf, voice.input, t0 + r() * (W.timeVar || 0.0015), {
          rate: 1 + (r() * 2 - 1) * (W.pitchVar || 0.03) * 0.7,
        });
      } else {
        try { buildGunshot(ac, voice.input, t0, r, W); } catch { /* ignore */ }
      }

      // duck everything else under the shot
      buses.duck(0.42 * closeness + 0.12, 0.02, mode === 'distant' ? 0.30 : 0.20);

      // supersonic crack for a round passing near the listener
      if (!own && W.supersonic !== false && o.dir) {
        supersonicCrack(worldPos, vec(o.dir).clone(), r, dist);
      }

      combatUntil = now() + 9;
      return voice;
    },

    /** A bullet impact on a surface from the CONTRACTS.md enum. */
    impact(surface, opts = {}) {
      if (!running) return null;
      const s = SURFACES[surface] ? surface : 'concrete';
      stats.impacts++;
      return playSound(`impact_${s}`, {
        priority: 0.55, send: 0.5, refDistance: 3,
        ...opts,
      });
    },

    /** One footstep. opts: { position, gait, foot, energy, volume } */
    footstep(surface, opts = {}) {
      if (!running) return null;
      const s = STEP_SURFACES[surface] ? surface : 'concrete';
      stats.steps++;
      const own = !opts.position;
      return playSound(`step_${s}`, {
        gait: opts.gait || gait,
        priority: own ? 0.6 : 0.35,
        refDistance: own ? 1000 : 2.5,
        maxDistance: 45,
        send: own ? 0.25 : 0.4,
        volume: (opts.volume ?? 1) * (own ? 0.55 : 1),
        spatial: !own,
        ...opts,
      });
    },

    /** Drive the listener from the camera. Called every frame by update(). */
    setListener(position, quaternion, velocity) {
      if (!running) return;
      try { spatial.setListener(position, quaternion, velocity); } catch { /* ignore */ }
    },

    /** Adaptive music state. */
    music(state) {
      const s = MUSIC_STATES.includes(state) ? state : null;
      if (s) {
        explicitMusic = (s === 'menu' || s === 'victory' || s === 'defeat') ? s : null;
        desiredMusic = s;
      }
      if (!running || !music) return;
      music.set(s || desiredMusic);
    },

    /* --- extensions (never remove the frozen members above) --------- */

    mute(on) {
      if (!buses) return;
      buses.setMaster(on ? 0 : (config.masterLevel ?? 0.85));
    },
    setMasterVolume(v) {
      config.masterLevel = v;
      if (buses) buses.setMaster(v);
    },
    setBusVolume(name, v) { if (buses) buses.setBusLevel(name, v); },
    /** Force a reverb zone (debug / scripted sequences). */
    setZone(name, immediate = true) { if (zones) zones.setZone(name, immediate); },
    get zone() { return zones ? zones.current : 'outdoor'; },
    zones: () => (zones ? zones.describe() : null),

    /** Trigger the concussion effect directly. */
    tinnitus(power = 1, seconds = 4.5) { if (buses) buses.tinnitus(power, seconds); },
    duck(amount, hold, release) { if (buses) buses.duck(amount, hold, release); },

    stats() {
      return {
        ...stats,
        voices: spatial ? spatial.voiceCount : 0,
        maxVoices: spatial ? spatial.maxVoices : 0,
        baked: bakedGuns.size + bakedBank.size,
        bakesDone: baker.done,
        bakesPending: baker.pending,
        contextState: ac ? ac.state : 'none',
        zone: zones ? zones.current : null,
        events: { ...eventCounts },
      };
    },

    /** Run the numeric self-test (offline; works with audio never resumed). */
    async selftest(opts = {}) {
      const m = await import('./selftest.js');
      const report = await m.runSelfTest({ createAudioSystem, ...opts });
      report.text = m.formatReport(report);
      return report;
    },

    /** Render + analyse the showcase sounds for the debug overlay. */
    async analyse() {
      if (analysisStarted) return analysisItems;
      analysisStarted = true;
      const m = await import('./selftest.js');
      analysisItems = [];
      overlay.setItems(analysisItems);
      await m.analyseShowcase((item) => {
        analysisItems.push(item);
        overlay.setItems(analysisItems);
        overlay.setStatus(debugStatus());
      });
      overlay.setItems(analysisItems);
      return analysisItems;
    },

    overlay,
    get baker() { return baker; },
  };

  /* ------------------------------------------------------------------ *
   * Composite behaviours
   * ------------------------------------------------------------------ */

  /** N-wave crack when a round passes close to the listener. */
  function supersonicCrack(origin, dir, r, shooterDist) {
    if (!origin || !dir || dir.lengthSq() < 1e-6) return;
    dir.normalize();
    _v2.copy(spatial.listener).sub(origin);
    const along = _v2.dot(dir);
    if (along < 2 || along > 400) return;
    const perp = Math.sqrt(Math.max(0, _v2.lengthSq() - along * along));
    if (perp > 10) return;
    const passPoint = origin.clone().addScaledVector(dir, along);
    const delay = along / BULLET_SPEED + perp / SPEED_OF_SOUND;
    const level = Math.max(0.08, 1 - perp / 10);
    later(Math.max(0, delay * 1000), () => {
      if (!running) return;
      if (perp < 2.2) {
        playSound('crack', { position: passPoint, volume: 0.85 * level, priority: 0.9, send: 0.35, refDistance: 3 });
        playSound('whizby', { position: passPoint, volume: 0.45 * level, priority: 0.6, delay: 0.01, send: 0.3 });
      } else {
        playSound('crack', { position: passPoint, volume: 0.6 * level, priority: 0.75, send: 0.4, refDistance: 4 });
      }
    });
    void shooterDist;
  }

  /** Ambient distant gunfire, fired from the ambience scheduler. */
  function distantGunfire() {
    if (!running) return;
    const cls = ['rifle', 'smg', 'rifle', 'sniper'][Math.floor(rng() * 4) % 4];
    const a = rng() * Math.PI * 2;
    const d = 90 + rng() * 180;
    const p = spatial.listener.clone().add(new THREE.Vector3(Math.cos(a) * d, rng() * 6 - 2, Math.sin(a) * d));
    const bursts = 1 + Math.floor(rng() * 4);
    for (let i = 0; i < bursts; i++) {
      later(i * (70 + rng() * 60), () => {
        if (!running) return;
        api.gunshot(cls, { position: p, own: false, volume: 0.55, seed: (rng() * 1e9) | 0 });
      });
    }
  }

  /** The whole reload timeline, scheduled as one cancellable sequence. */
  function reloadSequence(phase, weapon) {
    const token = ++reloadToken;
    const cls = classify(weapon);
    const fast = cls === 'smg' || cls === 'pistol';
    const k = fast ? 0.82 : 1;
    const step = (ms, name, opts) => later(ms * k, () => { if (reloadToken === token) playSound(name, opts); });

    if (phase === 'end') {
      step(0, 'bolt_release', { volume: 0.9, spatial: false, send: 0.3 });
      step(180, 'cloth', { volume: 0.5, spatial: false, send: 0.25 });
      return;
    }
    if (cls === 'shotgun') {
      // shell-by-shell: a short pump plus a handful of insertions
      step(0, 'bolt_back', { volume: 0.7, spatial: false, send: 0.3 });
      for (let i = 0; i < 4; i++) {
        step(220 + i * 320, 'mag_in', { volume: 0.55, spatial: false, send: 0.25 });
        step(330 + i * 320, 'selector', { volume: 0.35, spatial: false, send: 0.2 });
      }
      step(1620, 'bolt_release', { volume: 0.85, spatial: false, send: 0.3 });
      return;
    }
    step(0, 'mag_release', { volume: 0.8, spatial: false, send: 0.25 });
    step(90, 'mag_out', { volume: 0.75, spatial: false, send: 0.3 });
    step(240, 'cloth', { volume: 0.4, spatial: false, send: 0.2 });
    step(560, 'mag_in', { volume: 0.8, spatial: false, send: 0.3 });
    step(880, 'mag_seat', { volume: 0.95, spatial: false, send: 0.35 });
    step(1080, 'gear', { volume: 0.5, spatial: false, send: 0.25 });
  }

  /* ------------------------------------------------------------------ *
   * Event bus wiring
   * ------------------------------------------------------------------ */

  function subscribe(events) {
    if (!events || typeof events.on !== 'function') return () => {};
    const offs = [];
    const on = (type, fn) => {
      offs.push(events.on(type, (p) => {
        try { fn(p || {}); } catch (err) {
          if (typeof console !== 'undefined' && !on._warned) {
            on._warned = true;
            console.warn('[audio] handler error', type, err);
          }
        }
      }));
    };

    on('weapon:fire', (p) => {
      eventCounts.fire++;
      if (!running) return;
      api.gunshot(p.weapon, { position: p.origin, dir: p.dir, seed: p.seed });
      const cls = classify(p.weapon);
      if (cls !== 'shotgun' && cls !== 'sniper') {
        playSound('shell_drop', { volume: 0.35, spatial: false, send: 0.25, priority: 0.2 });
      }
    });

    on('weapon:reload', (p) => {
      eventCounts.reload++;
      if (!running) return;
      reloadSequence(p.phase === 'end' ? 'end' : 'start', p.weapon);
    });

    on('weapon:switch', (p) => {
      if (!running) return;
      reloadToken++;                      // a switch cancels an in-flight reload
      playSound('weapon_lower', { volume: 0.8, spatial: false, send: 0.25 });
      later(220, () => playSound('weapon_raise', { volume: 0.85, spatial: false, send: 0.25 }));
      later(120, () => playSound('sling', { volume: 0.5, spatial: false, send: 0.25 }));
      void p;
    });

    on('ballistics:hit', (p) => {
      eventCounts.hit++;
      if (!running) return;
      const surface = p.surface || 'concrete';
      api.impact(surface, { position: p.point, volume: 1, seed: p.seed });
      if (p.penetrated) playSound('penetration', { position: p.point, volume: 0.5, delay: 0.01 });
      if (p.entity) playSound('hitmarker', { volume: 0.7, spatial: false });
    });

    on('damage:dealt', (p) => {
      if (!running) return;
      if (p.killed) playSound('kill_confirm', { volume: 0.8, spatial: false });
      else if (p.headshot) playSound('headshot', { volume: 0.8, spatial: false });
      else playSound('hitmarker', { volume: 0.7, spatial: false });
      combatUntil = now() + 9;
    });

    on('damage:taken', (p) => {
      eventCounts.damage++;
      if (!running) return;
      const amt = Math.max(0.15, Math.min(1.4, (p.amount ?? 20) / 25));
      playSound('damage_hit', { volume: 0.55 * amt, spatial: false, send: 0.2, priority: 0.9 });
      buses.duck(0.35 * amt, 0.05, 0.4);
      if ((p.health ?? 100) < 30) buses.tinnitus(0.35, 2.6);
      combatUntil = now() + 9;
    });

    on('player:footstep', (p) => {
      eventCounts.step++;
      if (!running) return;
      api.footstep(p.surface || 'concrete', {
        position: p.position && spatial.listener.distanceTo(vec(p.position) || spatial.listener) > 2.5 ? p.position : null,
        foot: p.foot, gait,
      });
    });

    on('player:land', (p) => {
      eventCounts.land++;
      if (!running) return;
      const surf = p.surface || 'concrete';
      playSound(STEP_SURFACES[surf] ? `land_${surf}` : 'land_concrete', {
        impact: p.impact ?? 4, volume: 1, spatial: false, send: 0.4, priority: 0.85,
      });
      const hard = Math.min(1, (p.impact ?? 4) / 12);
      if (hard > 0.55) buses.duck(0.25 * hard, 0.02, 0.25);
    });

    on('player:state', (p) => {
      const st = p.state || 'idle';
      playerSpeed = p.speed ?? playerSpeed;
      crouched = !!p.crouched;
      gait = crouched ? 'crouch' : (st === 'sprint' ? 'sprint' : st === 'run' ? 'run'
        : st === 'slide' ? 'slide' : st === 'air' ? 'air' : st === 'crouch' ? 'crouch' : 'walk');
      if (!running) { lastState = st; return; }
      if (st !== lastState) {
        if (st === 'slide') playSound('slide', { volume: 0.8, spatial: false, send: 0.4, seconds: 0.9 });
        else if (st === 'sprint' || st === 'run') ambience.movement(0.7);
        else if (st === 'crouch') playSound('cloth', { volume: 0.45, spatial: false, send: 0.2 });
        lastState = st;
      }
      if (ambience) ambience.setIntensity(1);
    });

    on('ai:spawn', () => { if (running) combatUntil = Math.max(combatUntil, now() + 4); });

    on('ai:death', (p) => {
      if (!running) return;
      const pos = vec(p.entity && (p.entity.position || p.position));
      playSound('impact_flesh', { position: pos ? pos.clone() : null, volume: 0.55, priority: 0.4 });
      playSound('kill_confirm', { volume: 0.5, spatial: false });
    });

    on('fx:explosion', (p) => {
      eventCounts.explosion++;
      if (!running) return;
      const pos = vec(p.position);
      const power = p.power ?? 1;
      const dist = pos ? spatial.listener.distanceTo(pos) : 0;
      playSound('explosion', {
        position: pos ? pos.clone() : null, volume: Math.min(1.2, 0.8 + power * 0.2),
        power, priority: 1, send: 0.7, refDistance: 12, maxDistance: 900, spatial: !!pos,
      });
      const radius = p.radius ?? 8;
      const prox = Math.max(0, 1 - dist / Math.max(4, radius * 3.2));
      buses.duck(0.6 + 0.3 * prox, 0.06, 0.7);
      if (prox > 0.25) buses.tinnitus(Math.min(1, prox * 1.25), 3.5 + 2.5 * prox);
      combatUntil = now() + 12;
    });

    return () => { for (const off of offs) { try { off(); } catch { /* ignore */ } } };
  }

  /* ------------------------------------------------------------------ *
   * Debug status + shot
   * ------------------------------------------------------------------ */

  function debugStatus() {
    return {
      contextState: ac ? ac.state : null,
      enabled: running,
      sampleRate: ac ? ac.sampleRate : 48000,
      voices: spatial ? spatial.voiceCount : 0,
      maxVoices: spatial ? spatial.maxVoices : (config.maxVoices ?? 44),
      baked: baker.done,
      pending: baker.pending,
      zones: zones ? zones.describe() : null,
      music: music ? music.state : (explicitMusic || desiredMusic),
      eventCounts,
    };
  }

  /**
   * Hide the overlay whenever a different shot is selected, so audio_debug can
   * never contaminate another team's screenshot. Additive wrapper; the spine file
   * is untouched.
   */
  function wrapSetShot() {
    if (shotWrapDone || typeof window === 'undefined' || !window.__GAME__) return;
    const g = window.__GAME__;
    if (typeof g.setShot !== 'function' || g.__audioShotWrapped) return;
    const orig = g.setShot.bind(g);
    g.setShot = (name) => {
      if (name !== 'audio_debug') overlay.show(false);
      return orig(name);
    };
    g.__audioShotWrapped = true;
    shotWrapDone = true;
  }

  /* ------------------------------------------------------------------ *
   * System
   * ------------------------------------------------------------------ */

  let unsubscribe = () => {};
  let gestureOff = () => {};

  return {
    name: 'audio',
    priority: 90,

    init(ctx) {
      ctxRef = ctx;
      ctx.audio = api;
      unsubscribe = subscribe(ctx.events);

      // Auto-start on the first real user gesture. Harmless — and never fires in
      // the headless capture run, which is exactly what the contract requires.
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        const kick = () => { api.resume(); };
        const types = ['pointerdown', 'mousedown', 'keydown', 'touchstart'];
        for (const t of types) window.addEventListener(t, kick, { once: true, passive: true });
        gestureOff = () => { for (const t of types) window.removeEventListener(t, kick); };
        if (!window.__AUDIO__) window.__AUDIO__ = api;   // never clobber the live instance
      }

      registerShot('audio_debug', (c) => {
        look(c, [13, 7.5, 13], [0, 1.2, 0], 55);
        overlay.setStatus(debugStatus());
        overlay.show(true);
        api.analyse();
      }, { settle: 10, desc: 'procedural audio diagnostics: spectra, levels, attack times, RT60' });

      // The offline analysis is kicked from the first few update() calls, i.e.
      // strictly after boot has finished, so it can never lengthen boot.
    },

    update(dt, ctx) {
      wrapSetShot();
      // Populate the debug overlay once the game is actually up. Deferring to the
      // third rendered frame guarantees this never competes with boot.
      if (!analysisStarted && config.eagerAnalysis !== false && ++framesSeen === 3) {
        later(0, () => { api.analyse(); });
      }
      if (overlay.visible) {
        overlay.setStatus(debugStatus());
        overlay.draw();
      }
      if (!running) return;

      // listener follows the camera
      const cam = ctx.camera;
      if (cam) {
        _v2.copy(cam.position);
        if (camVelInit && dt > 1e-5) _v2.sub(_prevCam).divideScalar(dt);
        else _v2.set(0, 0, 0);
        _prevCam.copy(cam.position);
        camVelInit = true;
        api.setListener(cam.position, cam.quaternion, _v2);
      }

      spatial.update();
      zones.update(dt, ctx, cam ? cam.position : spatial.listener);
      ambience.setIndoor(zones.indoor);
      ambience.update(dt);
      ambience.combat = now() < combatUntil ? 1 : 0;
      music.update();

      if (!explicitMusic) {
        const want = now() < combatUntil ? 'combat' : 'ambient';
        if (want !== desiredMusic) { desiredMusic = want; music.set(want); }
      }
    },

    dispose() {
      disposed = true;
      running = false;
      clearTimers();
      try { unsubscribe(); } catch { /* ignore */ }
      try { gestureOff(); } catch { /* ignore */ }
      try { overlay.dispose(); } catch { /* ignore */ }
      try { ambience && ambience.dispose(); } catch { /* ignore */ }
      try { music && music.dispose(); } catch { /* ignore */ }
      try { spatial && spatial.dispose(); } catch { /* ignore */ }
      try { buses && buses.dispose(); } catch { /* ignore */ }
      try { ac && ac.close(); } catch { /* ignore */ }
      ac = null;
    },
  };
}

export { WEAPON_CLASSES, ZONE_ORDER };
